export const EXPERT_BAND_DOMAIN = Object.freeze({ min: 0, max: 100 });

export function formatExpertBandValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Not available";
  return numeric.toFixed(1).replace(/\.0$/, "");
}

export function formatPunchListBandValue(value) {
  const normalized = normalizePunchListBandValue(value);
  return normalized === undefined ? "Not available" : String(normalized);
}

const finite = value => Number.isFinite(Number(value)) ? Number(value) : undefined;
const bound = value => Math.min(EXPERT_BAND_DOMAIN.max, Math.max(EXPERT_BAND_DOMAIN.min, value));

export function normalizePunchListBandValue(value) {
  const numeric = finite(value);
  return numeric === undefined ? undefined : Math.round(numeric);
}

export function deriveExpertBandPresentation({ reference, learnerValue, outerBounds }) {
  const observations = (reference?.values || []).map(Number).filter(Number.isFinite);
  const observedLow = finite(reference?.min) ?? (observations.length ? Math.min(...observations) : undefined);
  const observedHigh = finite(reference?.max) ?? (observations.length ? Math.max(...observations) : undefined);
  const mean = finite(reference?.mean);
  const sd = finite(reference?.sd);
  const n = finite(reference?.n) ?? observations.length;
  const learner = finite(learnerValue);
  if ([observedLow, observedHigh, mean, sd, learner].some(value => value === undefined)) return null;

  const rawCoreLow = mean - sd;
  const rawCoreHigh = mean + sd;
  const suppliedOuterLow = finite(outerBounds?.low);
  const suppliedOuterHigh = finite(outerBounds?.high);
  const rawOuterLow = suppliedOuterLow ?? observedLow;
  const rawOuterHigh = suppliedOuterHigh ?? observedHigh;
  if (observedLow > observedHigh || rawOuterLow > rawOuterHigh) return null;

  return {
    raw: {
      scaleMin: EXPERT_BAND_DOMAIN.min,
      scaleMax: EXPERT_BAND_DOMAIN.max,
      observedLow,
      observedHigh,
      mean,
      sd,
      learnerValue: learner,
      observations: observations.slice(),
      n,
      rawCoreLow,
      rawCoreHigh,
      outerLow: rawOuterLow,
      outerHigh: rawOuterHigh,
    },
    display: {
      coreLow: bound(rawCoreLow),
      coreHigh: bound(rawCoreHigh),
      outerLow: bound(rawOuterLow),
      outerHigh: bound(rawOuterHigh),
    },
  };
}

function replaceTokens(template, values) {
  return Object.entries(values).reduce(
    (text, [token, value]) => text.replaceAll(`{${token}}`, String(value)),
    template
  );
}

const PUNCHLIST_SCALE_LOCATION_BLOCKS = Object.freeze([
  { max: 19, blockId: "PL19-FB-03" },
  { max: 39, blockId: "PL19-FB-04" },
  { max: 59, blockId: "PL19-FB-05" },
  { max: 79, blockId: "PL19-FB-06" },
  { max: 100, blockId: "PL19-FB-07" },
]);

const LOW_END_MAX = 19;
const NARROW_BAND_MAX_WIDTH = 14;
const MIDDLE_SCALE_MIN = 35;
const MIDDLE_SCALE_MAX = 65;
const NEAR_MISS_MAX_DISTANCE = 3;
const LOW_END_BLOCK_BY_PARAMETER = Object.freeze({
  "overall severity": "PL26-FB-B8-LOW-OVERALL-SEVERITY",
  roughness: "PL26-FB-B8-LOW-ROUGHNESS",
  breathiness: "PL26-FB-B8-LOW-BREATHINESS",
  strain: "PL26-FB-B8-LOW-STRAIN",
});

export function resolvePunchListAgreementExplanation({ presentation, parameter, copy = {} }) {
  if (!presentation) return null;
  const { observedLow, observedHigh, mean } = presentation.raw;
  const normalizedParameter = String(parameter || "").trim().toLowerCase();
  let blockId;
  let kind = "scale-location";

  if (observedHigh <= LOW_END_MAX) {
    blockId = LOW_END_BLOCK_BY_PARAMETER[normalizedParameter] || "PL26-FB-B8-LOW-VOICE";
    kind = "low-end";
  } else if (
    observedHigh - observedLow <= NARROW_BAND_MAX_WIDTH &&
    (observedLow + observedHigh) / 2 >= MIDDLE_SCALE_MIN &&
    (observedLow + observedHigh) / 2 <= MIDDLE_SCALE_MAX
  ) {
    blockId = "PL26-FB-B8-MIDDLE-NARROW";
    kind = "middle-narrow";
  } else {
    blockId = PUNCHLIST_SCALE_LOCATION_BLOCKS.find(entry => mean <= entry.max)?.blockId;
    if (blockId === "PL19-FB-03") blockId = undefined;
  }

  const text = copy[blockId] || "";
  return text ? { blockId, kind, text } : null;
}

export function resolveSharedLowEndAgreement({ explanations = [], copy = {} }) {
  const expectedParameters = new Set(["overall severity", "roughness", "breathiness", "strain"]);
  const actualParameters = new Set(explanations.map(item => String(item.parameter || "").trim().toLowerCase()));
  if (
    explanations.length !== expectedParameters.size ||
    explanations.some(item => item.explanation?.kind !== "low-end") ||
    [...expectedParameters].some(parameter => !actualParameters.has(parameter))
  ) return null;
  const blockId = "PL26-FB-B8-LOW-VOICE";
  return copy[blockId] ? { blockId, text: copy[blockId] } : null;
}

export function resolveSharedAgreementCommentary({ explanations = [] }) {
  const applicable = explanations.filter(item => item.explanation?.text);
  if (applicable.length < 2) return null;
  const text = applicable[0].explanation.text;
  if (!applicable.every(item => item.explanation.text === text)) return null;
  return { blockId: applicable[0].explanation.blockId, text };
}

export function deriveSmallestGovernedObservationGap(observations = []) {
  const ordered = observations
    .filter(value => value !== null && value !== undefined && value !== "")
    .map(finite)
    .filter(value => value !== undefined)
    .sort((a, b) => a - b);
  if (ordered.length < 2) return null;
  return ordered.slice(1).reduce(
    (smallest, value, index) => Math.min(smallest, value - ordered[index]),
    Number.POSITIVE_INFINITY
  );
}

export function resolvePunchListFeedbackCaption({
  presentation,
  presentationVariant = "ordinary",
  parameter,
  copy = {},
  includeAgreement = true,
}) {
  if (!presentation) return [];
  const { raw, display } = presentation;
  const stage3 = presentationVariant === "stage3";
  const agreement = resolvePunchListAgreementExplanation({ presentation, parameter, copy });
  const displayedOuterLow = normalizePunchListBandValue(raw.outerLow);
  const displayedOuterHigh = normalizePunchListBandValue(raw.outerHigh);
  const displayedObservedLow = normalizePunchListBandValue(raw.observedLow);
  const displayedObservedHigh = normalizePunchListBandValue(raw.observedHigh);
  let positionBlockId;
  if (raw.learnerValue < displayedOuterLow) {
    positionBlockId = displayedOuterLow - raw.learnerValue <= NEAR_MISS_MAX_DISTANCE
      ? "CR22-FB-NEAR-MISS-BELOW"
      : "PL19-FB-11";
  }
  else if (raw.learnerValue > displayedOuterHigh) {
    positionBlockId = raw.learnerValue - displayedOuterHigh <= NEAR_MISS_MAX_DISTANCE
      ? "CR22-FB-NEAR-MISS-ABOVE"
      : "PL19-FB-12";
  }
  else if (raw.learnerValue < displayedObservedLow) positionBlockId = "PL26-FB-AGREEMENT-BELOW-OBSERVED";
  else if (raw.learnerValue > displayedObservedHigh) positionBlockId = "PL26-FB-AGREEMENT-ABOVE-OBSERVED";
  else if (raw.learnerValue >= raw.rawCoreLow && raw.learnerValue <= raw.rawCoreHigh) positionBlockId = "PL19-FB-08";
  else if (stage3) positionBlockId = "PL19-FB-13";
  else if (raw.learnerValue < raw.rawCoreLow) positionBlockId = "PL19-FB-09";
  else positionBlockId = "PL19-FB-10";
  const firstBlockId = stage3 ? "PL19-FB-02" : "PL19-FB-01";
  const values = {
    low: formatPunchListBandValue(raw.observedLow),
    high: formatPunchListBandValue(raw.observedHigh),
    coreLow: formatPunchListBandValue(display.coreLow),
    coreHigh: formatPunchListBandValue(display.coreHigh),
    observedLow: formatPunchListBandValue(raw.observedLow),
    observedHigh: formatPunchListBandValue(raw.observedHigh),
    mean: formatPunchListBandValue(raw.mean),
  };
  if (positionBlockId === "CR22-FB-NEAR-MISS-BELOW") {
    values.distance = formatPunchListBandValue(displayedOuterLow - raw.learnerValue);
  } else if (positionBlockId === "CR22-FB-NEAR-MISS-ABOVE") {
    values.distance = formatPunchListBandValue(raw.learnerValue - displayedOuterHigh);
  } else if (positionBlockId === "PL26-FB-AGREEMENT-BELOW-OBSERVED") {
    values.distance = formatPunchListBandValue(displayedObservedLow - raw.learnerValue);
  } else if (positionBlockId === "PL26-FB-AGREEMENT-ABOVE-OBSERVED") {
    values.distance = formatPunchListBandValue(raw.learnerValue - displayedObservedHigh);
  }
  if (values.distance !== undefined) {
    const numericDistance = Number(values.distance);
    values.pointOrPoints = numericDistance === 1 ? "point" : "points";
    const smallestGap = deriveSmallestGovernedObservationGap(raw.observations);
    const insideAgreementOutsideObserved = positionBlockId === "PL26-FB-AGREEMENT-BELOW-OBSERVED" ||
      positionBlockId === "PL26-FB-AGREEMENT-ABOVE-OBSERVED";
    values.closerThanPanelGap = insideAgreementOutsideObserved && smallestGap !== null && numericDistance < smallestGap
      ? ", and closer to them than they are to one another"
      : "";
  }
  const sentences = [{ blockId: firstBlockId, text: copy[firstBlockId] || "" }];
  if (includeAgreement && agreement) sentences.push(agreement);
  sentences.push({ blockId: positionBlockId, text: copy[positionBlockId] || "" });
  return sentences.map(sentence => ({
    ...sentence,
    text: replaceTokens(sentence.text, values),
  })).filter(sentence => sentence.text);
}

export function resolveExpertBandNarrative({ presentation, narrative }) {
  if (!presentation || !narrative) return null;
  const { learnerValue, observedLow, observedHigh, rawCoreLow: coreLow, rawCoreHigh: coreHigh } = presentation.raw;
  let branch;
  let direction;
  if (learnerValue >= coreLow && learnerValue <= coreHigh) {
    branch = narrative.insideCore;
  } else if (learnerValue >= observedLow && learnerValue <= observedHigh) {
    branch = narrative.insideObservedOutsideCore;
    direction = learnerValue < coreLow ? "lower" : "upper";
  } else {
    branch = narrative.outsideObserved;
    direction = learnerValue < observedLow ? "below" : "above";
  }
  return {
    blockId: branch.blockId,
    text: replaceTokens(branch.message, {
      low: formatExpertBandValue(observedLow),
      high: formatExpertBandValue(observedHigh),
      "core low": formatExpertBandValue(coreLow),
      "core high": formatExpertBandValue(coreHigh),
      rating: formatExpertBandValue(learnerValue),
      "upper or lower": direction,
      "above or below": direction,
    }),
  };
}
